// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — the tap input (principle #19: the control fits the game). Taking a specific stone off a
// layout is a precise point-and-pick, so the scheme is: TAP an exposed stone to take it, TAP the
// scree pile to draw. Hit areas are ≥44px, independent of the drawn size. Desktop keeps a full
// keyboard a11y path: arrows / Tab move a cursor across the EXPOSED stones, Enter/Space takes the
// highlighted one, D draws. Pointer Events only, clientX/Y − getBoundingClientRect (never offset).

export interface TapDeps {
  stoneAt: (cssX: number, cssY: number) => number | null;
  stockAt: (cssX: number, cssY: number) => boolean;
  exposedCenters: () => Array<{ id: number; x: number; y: number }>;
  onTake: (id: number) => void;
  onDraw: () => void;
  onSelect: (id: number | null) => void;
}

export interface TapInput {
  dispose(): void;
}

export function createTapInput(host: HTMLElement, deps: TapDeps): TapInput {
  let selected: number | null = null;

  const local = (e: PointerEvent): { x: number; y: number } => {
    const r = host.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e: PointerEvent): void => {
    if (e.button !== undefined && e.button !== 0) return;
    const p = local(e);
    const id = deps.stoneAt(p.x, p.y);
    if (id !== null) {
      e.preventDefault();
      selected = id;
      deps.onSelect(id);
      deps.onTake(id);
      return;
    }
    if (deps.stockAt(p.x, p.y)) {
      e.preventDefault();
      deps.onDraw();
    }
  };

  const clampSelected = (): void => {
    const centers = deps.exposedCenters();
    if (centers.length === 0) {
      selected = null;
      return;
    }
    if (selected === null || !centers.some((c) => c.id === selected)) {
      selected = centers[0].id;
    }
  };

  const step = (dx: number, dy: number): void => {
    const centers = deps.exposedCenters();
    if (centers.length === 0) return;
    clampSelected();
    const from = centers.find((c) => c.id === selected)!;
    let best = -1;
    let bestScore = Infinity;
    for (let i = 0; i < centers.length; i++) {
      if (centers[i].id === selected) continue;
      const vx = centers[i].x - from.x;
      const vy = centers[i].y - from.y;
      const along = vx * dx + vy * dy;
      if (along <= 0) continue;
      const perp = Math.abs(vx * dy - vy * dx);
      const score = perp * 2 + Math.hypot(vx, vy);
      if (score < bestScore) {
        bestScore = score;
        best = centers[i].id;
      }
    }
    if (best >= 0) selected = best;
    else selected = centers[(centers.findIndex((c) => c.id === selected) + 1) % centers.length].id;
    deps.onSelect(selected);
  };

  const onKey = (e: KeyboardEvent): void => {
    const k = e.key;
    if (k === 'd' || k === 'D') {
      e.preventDefault();
      deps.onDraw();
      return;
    }
    if (k === 'Enter' || k === ' ' || k === 'Spacebar') {
      e.preventDefault();
      clampSelected();
      if (selected !== null) deps.onTake(selected);
      return;
    }
    if (k === 'Tab') {
      e.preventDefault();
      const centers = deps.exposedCenters();
      if (centers.length) {
        clampSelected();
        const i = centers.findIndex((c) => c.id === selected);
        selected = centers[(i + (e.shiftKey ? -1 : 1) + centers.length) % centers.length].id;
        deps.onSelect(selected);
      }
      return;
    }
    let dx = 0;
    let dy = 0;
    if (k === 'ArrowLeft') dx = -1;
    else if (k === 'ArrowRight') dx = 1;
    else if (k === 'ArrowUp') dy = -1;
    else if (k === 'ArrowDown') dy = 1;
    else return;
    e.preventDefault();
    step(dx, dy);
  };

  host.addEventListener('pointerdown', onDown);
  window.addEventListener('keydown', onKey);

  return {
    dispose() {
      host.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    },
  };
}
